import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateEx } from '../../../functions/date-ex';
import {
  MONTHS_SHORT,
  formatThreeMonthRange,
  getDefaultThreeMonthAnchor,
  getThreeMonthBounds,
} from '../../../functions/todo/diary-mapper';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ToggleSwitchComponent } from '../../core/controls/toggle-switch.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';

interface DayCell {
  date: Date;
  inMonth: boolean;
  pastDue: number;
  open: number;
  completed: number;
}

interface MonthPanel {
  label: string;
  weeks: DayCell[][];
}

@Component({
  selector: 'app-diary-three-month',
  imports: [
    CommonModule,
    ButtonComponent,
    ToggleSwitchComponent,
    LoadingPanelComponent,
  ],
  template: `
    <div class="tw-flex tw-flex-col tw-w-full tw-gap-2">
      <div
        class="tw-flex tw-flex-wrap tw-items-center tw-justify-center sm:tw-justify-between tw-w-full tw-gap-2"
      >
        <div
          class="tw-flex tw-justify-center sm:tw-justify-start tw-min-w-[160px]"
        >
          <button
            type="button"
            class="tw-font-bold tw-underline tw-text-[var(--sed-link-color)] tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0 tw-whitespace-nowrap"
            (click)="goToCurrentThreeMonths()"
          >
            Return to current 3 months
          </button>
        </div>
        <span
          class="tw-inline-flex tw-items-center tw-justify-center tw-w-full sm:tw-w-auto tw-gap-1"
        >
          <app-button
            icon="fa-light fa-chevron-left"
            classes="calendar-button calendar-left"
            label=""
            ariaLabel="Previous 3 months"
            title="Previous 3 months"
            (click)="goToPreviousMonth()"
          />
          <b
            class="tw-text-[15px] tw-text-center tw-min-w-[140px]"
            role="status"
            aria-live="polite"
            >{{ formattedRange }}</b
          >
          <app-button
            icon="fa-light fa-chevron-right"
            classes="calendar-button calendar-right"
            label=""
            ariaLabel="Next 3 months"
            title="Next 3 months"
            (click)="goToNextMonth()"
          />
        </span>
        <div
          class="tw-flex tw-items-center tw-justify-center sm:tw-justify-end tw-w-full sm:tw-w-auto tw-gap-2"
        >
          <app-toggle-switch
            [control]="control"
            label="Include completed"
            [showHelpText]="false"
          />
          <ng-content select="[diaryToolbarRight]" />
        </div>
      </div>

      <div
        class="tw-flex tw-flex-wrap tw-gap-4 tw-items-center"
        role="list"
        aria-label="Diary due date legend"
      >
        <div class="tw-flex tw-items-center tw-gap-2" role="listitem">
          <span
            class="tw-w-4 tw-h-4 tw-rounded-full tw-bg-[var(--sed-status-red-color)]"
            aria-hidden="true"
          ></span>
          <span class="tw-text-sm tw-font-semibold">Past due</span>
        </div>
        <div class="tw-flex tw-items-center tw-gap-2" role="listitem">
          <span
            class="tw-w-4 tw-h-4 tw-rounded-full tw-bg-[var(--oi-accent-bar,#2563eb)]"
            aria-hidden="true"
          ></span>
          <span class="tw-text-sm tw-font-semibold">Open</span>
        </div>
        @if (includeCompleted) {
          <div class="tw-flex tw-items-center tw-gap-2" role="listitem">
            <span
              class="tw-w-4 tw-h-4 tw-rounded-full tw-bg-[var(--sed-sedgwick-green-color)]"
              aria-hidden="true"
            ></span>
            <span class="tw-text-sm tw-font-semibold">Completed</span>
          </div>
        }
      </div>

      <app-loading-panel
        loadingStyle="Box"
        [loadingHeight]="150"
        [state]="loading ? 'Loading' : 'Loaded'"
        class="tw-w-full"
      >
        <ng-template #loaded>
          <div
            class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6 tw-w-full"
            role="group"
            data-calendar-grid
            [attr.aria-label]="'Diary calendar, ' + formattedRange"
          >
            @for (month of monthPanels; track month.label) {
              <div class="tw-min-w-0">
                <div class="tw-font-semibold tw-text-[15px] tw-mb-2">
                  {{ month.label }}
                </div>
                <table class="tw-w-full tw-border-collapse tw-text-center">
                  <thead>
                    <tr>
                      @for (dow of weekDays; track dow) {
                        <th scope="col" class="tw-text-xs tw-font-semibold tw-pb-1">
                          {{ dow }}
                        </th>
                      }
                    </tr>
                  </thead>
                  <tbody>
                    @for (week of month.weeks; track $index) {
                      <tr>
                        @for (day of week; track day.date) {
                          <td class="tw-p-0.5">
                            @if (day.inMonth) {
                              <button
                                type="button"
                                class="day-button tw-w-8 tw-h-8 tw-rounded-full tw-border-0 tw-text-sm tw-mx-auto tw-flex tw-items-center tw-justify-center"
                                [ngClass]="{
                                  'tw-ring-2 tw-ring-[var(--sed-engaged-black-color)]':
                                    isSelected(day.date),
                                  'tw-bg-[var(--sed-status-red-color)] day-past':
                                    dayStyle(day) === 'past',
                                  'tw-bg-[var(--oi-accent-bar,#2563eb)] tw-text-white':
                                    dayStyle(day) === 'open',
                                  'tw-bg-[var(--sed-sedgwick-green-color)] tw-text-[var(--sed-engaged-black-color)]':
                                    dayStyle(day) === 'completed',
                                }"
                                [attr.data-date]="dayKey(day.date)"
                                [attr.tabindex]="dayTabIndex(day.date)"
                                [attr.aria-pressed]="isSelected(day.date)"
                                [attr.aria-label]="dayAriaLabel(day)"
                                (keydown)="onDayKeydown($event, day.date)"
                                (click)="onDayClick(day.date)"
                              >
                                {{ day.date.getDate() }}
                              </button>
                            }
                          </td>
                        }
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>
        </ng-template>
      </app-loading-panel>
    </div>
  `,
  styles: `
    .day-past {
      color: var(--sed-warm-white-color) !important;
    }

    .day-button:focus-visible {
      outline: 2px solid var(--sed-engaged-black-color);
      outline-offset: 3px;
    }
  `,
})
export class DiaryThreeMonthComponent
  extends BaseComponent
  implements OnInit, OnChanges, AfterViewChecked
{
  @Input() diaryData: any[] = [];
  @Input() loading = false;
  @Input() selectedDate: string | null = null;

  @Output() includeCompletedChange = new EventEmitter<boolean>();
  @Output() calendarAction = new EventEmitter<{
    action: string;
    periodAnchor: Date;
    startDate: Date;
    endDate: Date;
    selectedDate?: Date;
  }>();

  protected readonly weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  private host = inject(ElementRef<HTMLElement>);
  /** Day reached by arrows or click. Tab returns here when it is still visible. */
  private focusDate: Date | null = null;
  /** Restored after the day buttons are recreated. */
  private focusKeyAfterRender: string | null = null;

  control = new FormControl(false);
  periodAnchor = getDefaultThreeMonthAnchor();
  formattedRange = '';
  monthPanels: MonthPanel[] = [];
  includeCompleted = false;

  ngOnInit(): void {
    this.dispose.subs = this.control.valueChanges.subscribe((value) => {
      this.includeCompleted = !!value;
      this.includeCompletedChange.emit(this.includeCompleted);
      this.buildMonthPanels();
    });
    this.setRange(this.periodAnchor);
    const bounds = getThreeMonthBounds(this.periodAnchor);

    // Defer the initial emits so they land after this change-detection pass
    // finishes; emitting synchronously here mutates parent-bound state
    // (e.g. a loading flag) after the parent's own bindings were already
    // checked for this pass, which trips NG0100.
    queueMicrotask(() => {
      this.includeCompletedChange.emit(this.control.value);
      this.calendarAction.emit({
        action: 'init',
        periodAnchor: this.periodAnchor,
        startDate: bounds.start,
        endDate: bounds.end,
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['diaryData'] || changes['selectedDate']) {
      this.buildMonthPanels();
    }
  }

  ngAfterViewChecked(): void {
    const key = this.focusKeyAfterRender;
    if (!key || this.loading) {
      return;
    }
    const grid = this.calendarGrid();
    if (!grid) {
      return;
    }
    const button = grid.querySelector(
      `button[data-date="${key}"]`,
    ) as HTMLButtonElement | null;
    this.focusKeyAfterRender = null;
    this.focusButton(button);
  }

  goToPreviousMonth(): void {
    this.periodAnchor = DateEx.addMonths(this.periodAnchor, -1);
    this.emitPeriodChange('previous');
  }

  goToNextMonth(): void {
    this.periodAnchor = DateEx.addMonths(this.periodAnchor, 1);
    this.emitPeriodChange('next');
  }

  goToCurrentThreeMonths(): void {
    this.periodAnchor = getDefaultThreeMonthAnchor();
    this.emitPeriodChange('today');
  }

  onDayClick(date: Date): void {
    this.focusDate = this.startOfDay(date);
    const bounds = getThreeMonthBounds(this.periodAnchor);
    this.calendarAction.emit({
      action: 'calendarClick',
      periodAnchor: this.periodAnchor,
      startDate: bounds.start,
      endDate: bounds.end,
      selectedDate: date,
    });
  }

  onDayKeydown(event: KeyboardEvent, date: Date): void {
    const offset = this.arrowOffset(event.key);
    if (offset === null) {
      return;
    }
    event.preventDefault();
    const target = this.addCalendarDays(date, offset);
    this.focusDate = target;
    if (this.isDateInWindow(target)) {
      this.focusDayButton(target);
      return;
    }
    this.focusKeyAfterRender = this.dayKey(target);
    if (this.isBeforeWindow(target)) {
      this.goToPreviousMonth();
    } else {
      this.goToNextMonth();
    }
  }

  dayKey(date: Date): string {
    const day = this.startOfDay(date);
    const month = String(day.getMonth() + 1).padStart(2, '0');
    const dayOfMonth = String(day.getDate()).padStart(2, '0');
    return `${day.getFullYear()}-${month}-${dayOfMonth}`;
  }

  dayTabIndex(date: Date): number {
    return this.dayKey(date) === this.dayKey(this.tabStopDate()) ? 0 : -1;
  }

  dayAriaLabel(day: DayCell): string {
    const date = DateEx.formatShortDate(day.date);
    switch (this.dayStyle(day)) {
      case 'past':
        return `${date}, Past due`;
      case 'open':
        return `${date}, Open`;
      case 'completed':
        return `${date}, Completed`;
      default:
        return date;
    }
  }

  isSelected(date: Date): boolean {
    if (this.selectedDate) {
      return DateEx.formatShortDate(date) === this.selectedDate;
    }
    return DateEx.dateEquals(date, new Date());
  }

  dayStyle(day: DayCell): 'past' | 'open' | 'completed' | 'none' {
    if (!day.pastDue && !day.open && !day.completed) {
      return 'none';
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cell = new Date(day.date);
    cell.setHours(0, 0, 0, 0);

    if (cell < today && day.pastDue > 0) {
      return 'past';
    }
    if (day.open > 0) {
      return 'open';
    }
    if (day.pastDue > 0) {
      return 'past';
    }
    if (day.completed > 0) {
      return 'completed';
    }
    return 'none';
  }

  private emitPeriodChange(action: string): void {
    this.setRange(this.periodAnchor);
    const bounds = getThreeMonthBounds(this.periodAnchor);
    this.calendarAction.emit({
      action,
      periodAnchor: this.periodAnchor,
      startDate: bounds.start,
      endDate: bounds.end,
    });
  }

  private setRange(anchor: Date): void {
    this.periodAnchor = anchor;
    this.formattedRange = formatThreeMonthRange(anchor);
    this.buildMonthPanels();
  }

  private buildMonthPanels(): void {
    this.captureFocusedDay();
    const panels: MonthPanel[] = [];
    for (let offset = -1; offset <= 1; offset++) {
      const monthDate = new Date(
        this.periodAnchor.getFullYear(),
        this.periodAnchor.getMonth() + offset,
        1,
      );
      panels.push({
        label: `${MONTHS_SHORT[monthDate.getMonth()]} ${monthDate.getFullYear()}`,
        weeks: this.buildWeeksForMonth(monthDate),
      });
    }
    this.monthPanels = panels;
  }

  private buildWeeksForMonth(monthDate: Date): DayCell[][] {
    const weeks: DayCell[][] = [];
    const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const cursor = new Date(first);
    cursor.setDate(cursor.getDate() - cursor.getDay());

    for (let w = 0; w < 6; w++) {
      const week: DayCell[] = [];
      for (let d = 0; d < 7; d++) {
        const cellDate = new Date(cursor);
        cellDate.setDate(cursor.getDate() + w * 7 + d);
        const inMonth = cellDate.getMonth() === monthDate.getMonth();
        week.push({
          date: cellDate,
          inMonth,
          ...this.countsForDate(cellDate),
        });
      }
      weeks.push(week);
    }
    return weeks;
  }

  private countsForDate(date: Date): {
    pastDue: number;
    open: number;
    completed: number;
  } {
    const counts = { pastDue: 0, open: 0, completed: 0 };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);

    for (const diary of this.diaryData ?? []) {
      const due = new Date(diary.dateDue);
      due.setHours(0, 0, 0, 0);
      if (due.getTime() !== target.getTime()) {
        continue;
      }
      const status = (diary.status || '').toLowerCase();
      if (status === 'completed') {
        if (this.includeCompleted) {
          counts.completed++;
        }
      } else if (due < today) {
        counts.pastDue++;
      } else {
        counts.open++;
      }
    }
    return counts;
  }

  private tabStopDate(): Date {
    if (this.focusDate && this.isDateInWindow(this.focusDate)) {
      return this.startOfDay(this.focusDate);
    }
    const selected = this.selectedDayInWindow();
    if (selected) {
      return selected;
    }
    const today = this.startOfDay(new Date());
    if (this.isDateInWindow(today)) {
      return today;
    }
    return this.startOfDay(getThreeMonthBounds(this.periodAnchor).start);
  }

  private selectedDayInWindow(): Date | null {
    if (!this.selectedDate) {
      return null;
    }
    for (const month of this.monthPanels) {
      for (const week of month.weeks) {
        for (const day of week) {
          if (
            day.inMonth &&
            DateEx.formatShortDate(day.date) === this.selectedDate
          ) {
            return this.startOfDay(day.date);
          }
        }
      }
    }
    return null;
  }

  private captureFocusedDay(): void {
    if (this.focusKeyAfterRender) {
      return;
    }
    const active = document.activeElement;
    if (!(active instanceof HTMLButtonElement)) {
      return;
    }
    const key = active.getAttribute('data-date');
    if (!key || !this.host.nativeElement.contains(active)) {
      return;
    }
    this.focusKeyAfterRender = key;
  }

  private focusDayButton(date: Date): void {
    const key = this.dayKey(date);
    const buttons = this.host.nativeElement.querySelectorAll(
      'button[data-date]',
    ) as NodeListOf<HTMLButtonElement>;
    let target: HTMLButtonElement | null = null;
    for (const button of buttons) {
      const isTarget = button.getAttribute('data-date') === key;
      button.tabIndex = isTarget ? 0 : -1;
      if (isTarget) {
        target = button;
      }
    }
    this.focusButton(target);
  }

  private calendarGrid(): HTMLElement | null {
    return this.host.nativeElement.querySelector(
      '[data-calendar-grid]',
    ) as HTMLElement | null;
  }

  private focusButton(button: HTMLButtonElement | null): void {
    if (!button || document.activeElement === button) {
      return;
    }
    button.focus({ focusVisible: true } as FocusOptions);
  }

  private arrowOffset(key: string): number | null {
    switch (key) {
      case 'ArrowLeft':
        return -1;
      case 'ArrowRight':
        return 1;
      case 'ArrowUp':
        return -7;
      case 'ArrowDown':
        return 7;
      default:
        return null;
    }
  }

  private addCalendarDays(date: Date, days: number): Date {
    const next = this.startOfDay(date);
    next.setDate(next.getDate() + days);
    return next;
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private isDateInWindow(date: Date): boolean {
    const bounds = getThreeMonthBounds(this.periodAnchor);
    return DateEx.dateBetween(date, bounds.start, bounds.end);
  }

  private isBeforeWindow(date: Date): boolean {
    const start = this.startOfDay(getThreeMonthBounds(this.periodAnchor).start);
    return this.startOfDay(date) < start;
  }
}
